import { create } from 'zustand';
import type { EmbeddedService } from '../../../types/embedding';

interface EmbeddingState {
  services: EmbeddedService[];
  activeServiceId: string | null;
  loadingStates: Record<string, boolean>;
  errorStates: Record<string, Error | null>;
  
  // Actions
  registerService: (service: EmbeddedService) => void;
  unregisterService: (serviceId: string) => void;
  setServices: (services: EmbeddedService[]) => void;
  setActiveService: (serviceId: string | null) => void;
  setLoading: (serviceId: string, isLoading: boolean) => void;
  setError: (serviceId: string, error: Error | null) => void;
  getService: (serviceId: string) => EmbeddedService | undefined;
}

export const useEmbeddingStore = create<EmbeddingState>((set, get) => ({
  services: [],
  activeServiceId: null,
  loadingStates: {},
  errorStates: {},

  registerService: (service) =>
    set((state) => {
      // Prevent duplicates
      if (state.services.some((s) => s.id === service.id)) {
        return state;
      }
      return { services: [...state.services, service] };
    }),

  unregisterService: (serviceId) =>
    set((state) => ({
      services: state.services.filter((s) => s.id !== serviceId),
      // If the active service is being unregistered, clear it
      activeServiceId: state.activeServiceId === serviceId ? null : state.activeServiceId,
    })),

  setServices: (services) =>
    set(() => ({ services })),

  setActiveService: (serviceId) =>
    set(() => ({ activeServiceId: serviceId })),

  setLoading: (serviceId, isLoading) =>
    set((state) => ({
      loadingStates: {
        ...state.loadingStates,
        [serviceId]: isLoading,
      },
    })),

  setError: (serviceId, error) =>
    set((state) => ({
      errorStates: {
        ...state.errorStates,
        [serviceId]: error,
      },
    })),

  getService: (serviceId) =>
    get().services.find((s) => s.id === serviceId),
}));
